import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { updateDeliveryDriver, deleteDeliveryDriver } from '@vn-propane/shared/client';
import type { UpdateDeliveryDriverRequest } from '@vn-propane/shared/client';
import { useDeliveryDriver } from '../DeliveryDriversLayout';

export default function DeliveryDriverSettings() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { driver, refreshDrivers } = useDeliveryDriver();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [settingsForm, setSettingsForm] = useState<UpdateDeliveryDriverRequest>({
    full_name: undefined,
    display_name: undefined,
    gender: undefined,
    birthdate: undefined,
  });

  useEffect(() => {
    if (driver) {
      setSettingsForm({
        full_name: driver.full_name,
        display_name: driver.display_name || undefined,
        gender: driver.gender || undefined,
        birthdate: driver.birthdate || undefined,
      });
    }
  }, [driver]);

  const handleUpdate = async () => {
    if (!orgId || !driver) return;

    const result = await updateDeliveryDriver({
      path: { org_id: orgId, id: driver.id },
      body: settingsForm,
    });

    if (result.error) {
      console.error('Failed to update delivery driver:', result.error);
      return;
    }

    refreshDrivers();
  };

  const handleDelete = async () => {
    if (!orgId || !driver) return;

    const result = await deleteDeliveryDriver({
      path: { org_id: orgId, id: driver.id },
    });

    if (result.error) {
      console.error('Failed to delete delivery driver:', result.error);
      return;
    }

    setIsDeleteOpen(false);
    setDeleteConfirmText('');
    navigate(`/organizations/${orgId}/drivers`);
    refreshDrivers();
  };

  if (!driver) {
    return null;
  }

  return (
    <>
      <div className="max-w-2xl">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('deliveryDrivers.organizationDisplayName')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('deliveryDrivers.displayNameDescription')}
            </p>

            <div className="space-y-2">
              <Label htmlFor="settings-display-name">{t('deliveryDrivers.displayName')}</Label>
              <Input
                id="settings-display-name"
                value={settingsForm.display_name || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, display_name: e.target.value || undefined })}
                placeholder={driver.full_name}
              />
            </div>
          </div>

          <hr className="my-6" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('deliveryDrivers.driverAccountInformation')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('deliveryDrivers.accountInfoDescription')}
            </p>

            <div className="space-y-2">
              <Label htmlFor="settings-name">{t('deliveryDrivers.fullNameRequired')}</Label>
              <Input
                id="settings-name"
                value={settingsForm.full_name || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, full_name: e.target.value || undefined })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-gender">{t('deliveryDrivers.gender')}</Label>
              <Select
                value={settingsForm.gender || ''}
                onValueChange={(value) => setSettingsForm({ ...settingsForm, gender: value || undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('deliveryDrivers.selectGender')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('deliveryDrivers.male')}</SelectItem>
                  <SelectItem value="female">{t('deliveryDrivers.female')}</SelectItem>
                  <SelectItem value="other">{t('deliveryDrivers.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-birthdate">{t('deliveryDrivers.birthdate')}</Label>
              <Input
                id="settings-birthdate"
                type="date"
                value={settingsForm.birthdate || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, birthdate: e.target.value || undefined })}
              />
            </div>

            <Button onClick={handleUpdate} disabled={!settingsForm.full_name}>
              {t('deliveryDrivers.saveChanges')}
            </Button>
          </div>

          <hr className="my-8" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-destructive">{t('deliveryDrivers.dangerZone')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('deliveryDrivers.deleteWarning')}
            </p>
            <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              {t('deliveryDrivers.deleteDriver')}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={(open) => {
        setIsDeleteOpen(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deliveryDrivers.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <Trans
                  i18nKey="admin:deliveryDrivers.deleteConfirmMessage"
                  values={{ name: driver.full_name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="admin:deliveryDrivers.deleteConfirmPrompt"
                  values={{ name: driver.full_name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('deliveryDrivers.typeToConfirm')}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConfirmText !== driver.full_name}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
